import React, { useState } from 'react';
import { Typography, Breadcrumb, Card, Form, Input, DatePicker, InputNumber, Select, Button, message, Divider, Slider } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const CreateCampaign = () => {
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [addrOptions, setAddrOptions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const { token } = useAuth();

  const saveDraft = async () => {
    try {
      await form.validateFields(['name']);
      setSaving(true);
      const values = form.getFieldsValue();
      const [start, end] = values.schedule || [];
      const payload = {
        name: values.name,
        start_date: start ? start.toISOString() : null,
        end_date: end ? end.toISOString() : null,
        budget: values.budget != null ? Number(values.budget) : null,
        status: 'draft',
      };
      const res = await fetch('/api/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let errText = 'Failed to save draft';
        try { const j = await res.json(); if (j?.error) errText = j.error; } catch {}
        throw new Error(errText);
      }
      message.success('Draft saved');
      window.location.href = '/client/campaigns';
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const saveScheduled = async () => {
    try {
      await form.validateFields(['name', 'schedule']);
      const values = form.getFieldsValue();
      const [start, end] = values.schedule || [];
      if (!start) throw new Error('Please select a start date');
      setSaving(true);
      const payload = {
        name: values.name,
        start_date: start ? start.toISOString() : null,
        end_date: end ? end.toISOString() : null,
        budget: values.budget != null ? Number(values.budget) : null,
        status: 'active',
      };
      const res = await fetch('/api/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let errText = 'Failed to schedule campaign';
        try { const j = await res.json(); if (j?.error) errText = j.error; } catch {}
        throw new Error(errText);
      }
      message.success('Campaign scheduled');
      window.location.href = '/client/campaigns';
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  const searchAddresses = async (q) => {
    try {
      if (!q || q.trim().length < 3) {
        setAddrOptions([]);
        return;
      }
      setAddrLoading(true);
      const res = await fetch(`/api/addresses/search?q=${encodeURIComponent(q)}&limit=8&countrycodes=us`);
      if (!res.ok) throw new Error('Address search failed');
      const data = await res.json();
      const options = (data.items || []).map((item) => ({
        value: item.display_name,
        label: item.display_name,
        item,
      }));
      setAddrOptions(options);
    } catch (e) {
      // Silent fail for UX; optional toast
      // message.error(e.message || 'Address search failed');
    } finally {
      setAddrLoading(false);
    }
  };

  const mapObjectiveToFacebook = (obj) => {
    switch (obj) {
      case 'conversions': return 'CONVERSIONS';
      case 'traffic': return 'LINK_CLICKS';
      case 'awareness': return 'AWARENESS';
      default: return 'CONVERSIONS';
    }
  };

  const pushFacebook = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const [start, end] = values.schedule || [];
      // Build geo targeting if address selected
      let targeting = { geo_locations: { countries: ['US'] } };
      const metas = Array.isArray(values.locations_meta) ? values.locations_meta : [];
      const radiusMiles = Number(values.radius_miles || 10);
      const radius = Math.max(1, Math.min(50, isNaN(radiusMiles) ? 10 : radiusMiles));
      const custom_locations = metas
        .filter((m) => m && m.lat != null && m.lon != null)
        .map((m) => ({
          latitude: Number(m.lat),
          longitude: Number(m.lon),
          radius,
          distance_unit: 'mile',
        }));
      if (custom_locations.length > 0) {
        targeting = { geo_locations: { custom_locations } };
      }
      const payload = {
        name: values.name,
        objective: mapObjectiveToFacebook(values.objective),
        status: 'PAUSED',
        daily_budget: Math.round(Number(values.budget || 0) * 100),
        start_time: start ? start.toISOString() : undefined,
        end_time: end ? end.toISOString() : undefined,
        targeting,
        page_id: values.page_id,
        link: values.link || 'https://example.com',
        message: values.message || 'Check this out!',
        caption: (values.link || 'example.com').replace(/^https?:\/\//, ''),
      };
      const res = await fetch('/api/ads/facebook/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json())?.error || 'Facebook push failed');
      message.success('Pushed to Facebook');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Facebook push failed');
    } finally {
      setSaving(false);
    }
  };

  const pushGoogle = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        name: values.name,
        daily_budget_micros: Math.round(Number(values.budget || 0) * 1_000_000),
        channel_type: 'SEARCH',
        status: 'PAUSED',
        bid_cpc_micros: 1_000_000,
        final_urls: [values.link || 'https://example.com'],
        headlines: values.headlines && values.headlines.length ? values.headlines : ['Great Product', 'Save Today'],
        descriptions: values.descriptions && values.descriptions.length ? values.descriptions : ['Free shipping.'],
      };
      const res = await fetch('/api/ads/google/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json())?.error || 'Google push failed');
      message.success('Pushed to Google Ads');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Google push failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Campaigns' }, { title: 'Create' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Create New Campaign</Title>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Fill in campaign details.</Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item label="Campaign Name" name="name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Fall Promotion" />
          </Form.Item>
          <Form.Item label="Objective" name="objective" rules={[{ required: true }]}>
            <Select placeholder="Select objective">
              <Option value="awareness">Awareness</Option>
              <Option value="traffic">Traffic</Option>
              <Option value="conversions">Conversions</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Schedule" name="schedule">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Budget (USD)" name="budget" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Target Locations" name="locations">
            <Select
              mode="multiple"
              showSearch
              style={{ width: '100%' }}
              options={addrOptions}
              onSearch={(val) => searchAddresses(val)}
              placeholder="Type to search US addresses; pick multiple"
              notFoundContent={addrLoading ? 'Searching…' : null}
              filterOption={false}
              onSelect={(value, option) => {
                const meta = option?.item || null;
                if (!meta) return;
                const current = form.getFieldValue('locations_meta') || [];
                const exists = current.some((m) => Number(m.lat) === Number(meta.lat) && Number(m.lon) === Number(meta.lon));
                if (!exists) form.setFieldsValue({ locations_meta: [...current, meta] });
              }}
              onDeselect={(value, option) => {
                const meta = option?.item || null;
                if (!meta) return;
                const current = form.getFieldValue('locations_meta') || [];
                const next = current.filter((m) => !(Number(m.lat) === Number(meta.lat) && Number(m.lon) === Number(meta.lon)));
                form.setFieldsValue({ locations_meta: next });
              }}
            />
          </Form.Item>
          <Form.Item label="Radius (miles)" name="radius_miles" tooltip="Used for Facebook custom location targeting" initialValue={10}>
            <Slider min={1} max={50} step={1} tooltip={{ formatter: (v) => `${v} mi` }} marks={{1:'1',10:'10',25:'25',50:'50'}}/>
          </Form.Item>
          {/* Hidden field to keep selected addresses meta (lat/lon, etc.) */}
          <Form.Item name="locations_meta" style={{ display: 'none' }} initialValue={[]}> 
            <Input type="hidden" />
          </Form.Item>
          <Divider>Optional (Creatives/Links)</Divider>
          <Form.Item label="Website Link" name="link">
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item label="Facebook Page ID" name="page_id">
            <Input placeholder="Required for FB object_story_spec" />
          </Form.Item>
          <Form.Item label="Ad Message (FB)" name="message">
            <Input placeholder="Primary text for FB link ad" />
          </Form.Item>
          <Form.Item label="Google Headlines" name="headlines" tooltip="Enter comma-separated headlines">
            <Input placeholder="e.g., Great Product, Save Today" onBlur={(e)=>{
              const v=e.target.value; if(!v) return; form.setFieldsValue({headlines: v.split(',').map(s=>s.trim()).filter(Boolean)});
            }} />
          </Form.Item>
          <Form.Item label="Google Descriptions" name="descriptions" tooltip="Enter comma-separated descriptions">
            <Input placeholder="e.g., Free shipping., 24/7 Support" onBlur={(e)=>{
              const v=e.target.value; if(!v) return; form.setFieldsValue({descriptions: v.split(',').map(s=>s.trim()).filter(Boolean)});
            }} />
          </Form.Item>
        </Form>
        <Divider />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={saveDraft} disabled={saving}>Save Draft</Button>
          <Button onClick={saveScheduled} type="primary" loading={saving}>Schedule</Button>
          <Button onClick={pushFacebook} loading={saving}>Push to Facebook</Button>
          <Button onClick={pushGoogle} loading={saving} type="primary">Push to Google</Button>
        </div>
      </Card>
    </div>
  );
};

export default CreateCampaign;
